import React from "react";
import { gql } from "@apollo/client";
import { useCanImpersonate } from "@src/features/app/hooks/useImpersonation";
import { useViewAs, viewAsLabel } from "@src/features/app/ViewAsDialog";
import useUser from "@src/features/app/hooks/useUser";
import { canViewAsPerson } from "@src/features/users/user-details/ViewAsMenuItem";
import { useDebouncedInputValue } from "@src/lib/hooks/useDebouncedInputValue";
import Text from "@src/lib/ui/Text";
import { Input } from "@src/lib/ui/Input";
import {
  AdminToolsPanel,
  AdminToolsEmptyRow,
  AdminToolsRowList,
} from "@src/features/app/top-nav/AdminToolsLayout";
import { Tooltip, TooltipContent, TooltipTrigger } from "@src/lib/ui/Tooltip";
import {
  SearchUsersForImpersonationQuery,
  useSearchUsersForImpersonationQuery,
} from "./ImpersonateUser.generated";

type Employee = SearchUsersForImpersonationQuery["People"]["objects"][number];

function formatEmployeeDetails(emp: Employee): string {
  return [
    `ID ${emp.id}`,
    emp.userType,
    emp.locations.map((l) => l.name).join(", "),
    emp.roles.map((r) => r.name).join(", "),
  ]
    .filter(Boolean)
    .join(" · ");
}

const PAGE_SIZE = 20;

const ImpersonateUser: React.FC = () => {
  const canImpersonate = useCanImpersonate();
  if (!canImpersonate) return null;
  return <ImpersonateUserAccordion />;
};

const ImpersonateUserAccordion: React.FC = () => {
  const {
    value: search,
    setValue: setSearch,
    debouncedValue: debouncedSearch,
  } = useDebouncedInputValue({ initialValue: "", delay: 300 });

  const { data, loading } = useSearchUsersForImpersonationQuery({
    variables: {
      input: {
        ...(debouncedSearch ? { search: { value: debouncedSearch } } : {}),
      },
      pagination: { limit: PAGE_SIZE },
    },
  });

  const { user } = useUser();
  // Leaves out yourself and Opus staff.
  const employees: Employee[] = (data?.People?.objects || []).filter((emp) =>
    canViewAsPerson(emp, user?.id),
  );
  const { viewAs, dialog: viewAsDialog } = useViewAs("admin_tools");

  return (
    <AdminToolsPanel
      toolbar={
        <Input
          type="text"
          placeholder="Search by name, email, or phone..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      }
    >
      <AdminToolsRowList>
        {loading && employees.length === 0 && (
          <AdminToolsEmptyRow>Loading...</AdminToolsEmptyRow>
        )}
        {employees.map((emp) => {
          const name = `${emp.firstName} ${emp.lastName}`;
          const details = formatEmployeeDetails(emp);
          return (
            <Tooltip key={emp.id}>
              <TooltipTrigger asChild>
                <button
                  className="flex w-full min-w-0 flex-col items-start gap-0.5 px-6 py-2.5 text-left hover:bg-state-hover"
                  aria-label={viewAsLabel(name)}
                  onClick={() =>
                    viewAs({
                      id: emp.id,
                      name,
                      userType: emp.userType,
                    })
                  }
                >
                  <Text variant="compact-title">{name}</Text>
                  <Text
                    variant="helper-description"
                    className="block w-full truncate text-text-secondary"
                  >
                    {details}
                  </Text>
                </button>
              </TooltipTrigger>
              <TooltipContent side="right">{`${name}, ${details}`}</TooltipContent>
            </Tooltip>
          );
        })}
        {!loading && employees.length === 0 && (
          <AdminToolsEmptyRow>No users found</AdminToolsEmptyRow>
        )}
      </AdminToolsRowList>
      {viewAsDialog}
    </AdminToolsPanel>
  );
};

gql`
  query SearchUsersForImpersonation(
    $input: GetPeopleInput!
    $pagination: PaginationInput
  ) {
    People(input: $input, pagination: $pagination) {
      totalCount
      objects {
        id
        firstName
        lastName
        userType
        email
        phoneNumber
        locations {
          id
          name
        }
        roles {
          id
          name
        }
      }
    }
  }
`;

export default ImpersonateUser;
